import { createFileRoute, Link } from "@tanstack/react-router";
import { MarketingPage, Section, ComparisonTable, Callout } from "@/components/marketing-page";
import {
  RedditGapsDemoBoard,
  RedditGapsDemoTracking,
} from "@/components/reddit-gaps-demo";
import { marketingHead, type FAQ } from "@/lib/seo";

const PATH = "/features/reddit-gaps";
const TITLE = "Reddit Gaps: Find Threads Recommending Competitors, Not You | monroya.ai";
const DESCRIPTION =
  "Reddit Gaps finds live Reddit threads where your competitors get recommended and your company is never mentioned, with upvotes, comments, subreddit rules, and AI-citation tracking on the threads you reply to.";

const FAQS: FAQ[] = [
  {
    q: "What is Reddit Gaps?",
    a: "Reddit Gaps is a standing list of live Reddit threads in your category where competitors are recommended by name and your company is never mentioned. Monroya derives the competitor set from how AI assistants answer buyer questions about your category, then searches Reddit for those names and filters out threads you can't act on.",
  },
  {
    q: "Why does Reddit matter for AI visibility?",
    a: "Reddit is one of the most frequently cited domains in AI answers about software and services. When ChatGPT or Perplexity answers \"what should I use for X\", it often pulls from a Reddit thread where real users named specific tools. If your competitors are named in those threads and you aren't, the model has no reason to surface you.",
  },
  {
    q: "How does Monroya avoid recommending archived or locked threads?",
    a: "Every candidate is age-checked and status-checked before it's listed. Threads older than 180 days are dropped, threads between 90 and 180 days old must show recent activity, and archived or locked threads are excluded entirely. The excluded counts are shown on the page so you can see exactly what was filtered and why.",
  },
  {
    q: "Does Monroya post to Reddit for me?",
    a: "No. Monroya finds the thread, shows the subreddit's posting constraints, and drafts a reply you can edit. You post it yourself from your own account. Automated posting violates most subreddit rules and Reddit's terms.",
  },
  {
    q: "How does the AI-citation tracking work?",
    a: "Check off a thread once you've replied to it. On every following prompt scan, Monroya compares the URLs each provider cited against your commented threads, matching on the Reddit post ID so short links, mobile links, and permalinks all resolve to the same thread. A match records the model and the prompt and flags the thread as Cited by AI.",
  },
  {
    q: "How can I find my brand mentions on Reddit?",
    a: "Search Reddit for your brand name and common misspellings, or use Reddit's own search with sort set to new. To do it continuously, Monroya scans for threads naming your competitors and checks whether your brand appears in the same thread, so you see both the mentions you have and the threads where you're absent. The free Reddit gap check runs one of these searches without an account.",
  },
  {
    q: "Which plans include Reddit Gaps?",
    a: "Growth ($199/mo) includes the top 10 threads per scan without CSV export. Intelligence ($299/mo) includes every thread found plus CSV export. Starter ($79/mo) does not include Reddit Gaps. The free plan includes the full Intelligence-level experience.",
  },
];

export const Route = createFileRoute("/features_/reddit-gaps")({
  head: () =>
    marketingHead({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Features", path: "/features" },
        { name: "Reddit Gaps", path: PATH },
      ],
      faqs: FAQS,
    }),
  component: RedditGapsFeaturePage,
});

function RedditGapsFeaturePage() {
  return (
    <MarketingPage
      stamp="Feature"
      title="Reddit Gaps."
      dek="The live Reddit threads recommending your competitors, where your company is never mentioned. Found automatically, filtered for threads you can still act on, and tracked through to the AI answer that cites them."
      updated="September 2026"
      faqs={FAQS}
      related={[
        { label: "Free Reddit gap check", to: "/reddit-check", description: "Run one without an account" },
        { label: "All features", to: "/features", description: "Everything Monroya does" },
        { label: "Pricing", to: "/pricing", description: "Growth vs Intelligence" },
        {
          label: "Reddit is your new SEO",
          to: "/blog/how-llms-actually-weigh-reddit-content-and-how-to-know-if",
          description: "Why community content drives B2B AI visibility",
        },
        {
          label: "Why Reddit and G2 are winning AI share of voice",
          to: "/blog/how-llms-actually-weigh-reddit-content-and-how-to-know-if",
          description: "Where the citations actually come from",
        },
      ]}
    >
      <Section heading="The problem" id="problem">
        <p>
          When a buyer asks ChatGPT or Perplexity which tool to use, the answer
          is frequently assembled from Reddit threads where real people named
          real products. Those threads are already written. They&apos;re already
          being cited. And in most categories, your competitors are in them and
          you are not.
        </p>
        <p>
          You can&apos;t fix that from your own website. The gap is in someone
          else&apos;s thread, and finding those threads by hand, across every
          competitor, every subreddit, every week, is not a job anyone actually
          does.
        </p>
      </Section>

      <Section heading="What Reddit Gaps shows you" id="board">
        <p>
          A standing list, refreshed on every scan. Each row is one live thread
          where at least one tracked competitor is named and your domain is
          absent, with the engagement data that tells you whether it&apos;s
          worth your time.
        </p>
        <div className="lg:-mx-24 xl:-mx-40">
          <RedditGapsDemoBoard id="demo-board" />
        </div>
        <p className="mt-3 text-sm text-muted-foreground">
          Demo data shown. Real accounts see their own domain, their own
          competitor set, and live Reddit engagement counts.
        </p>
        <ul className="list-disc space-y-1 pl-6">
          <li>
            <strong className="text-ink">Upvotes and comment counts</strong> - 
            pulled live, so you can tell a 400-upvote thread from a dead one.
          </li>
          <li>
            <strong className="text-ink">Thread age</strong> - how long ago it
            was posted, because a reply on a two-year-old thread does nothing.
          </li>
          <li>
            <strong className="text-ink">Which competitors are named</strong> - 
            filterable, so you can work one rival at a time.
          </li>
          <li>
            <strong className="text-ink">Subreddit posting rules</strong> - the
            promo and karma constraints for that specific subreddit, before you
            write anything.
          </li>
          <li>
            <strong className="text-ink">Potential visibility</strong> - a
            rolled-up read on whether this week&apos;s thread supply is worth
            working.
          </li>
        </ul>
      </Section>

      <Section heading="Track the reply through to the citation" id="tracking">
        <p>
          Finding the thread is half the job. The half nobody measures is
          whether the reply did anything. Check off a thread once you&apos;ve
          commented and Monroya watches two outcomes for it.
        </p>
        <div className="lg:-mx-24 xl:-mx-40">
          <RedditGapsDemoTracking id="demo-tracking" />
        </div>
        <ol className="mt-4 list-decimal space-y-2 pl-6">
          <li>
            <strong className="text-ink">Cited by AI.</strong> Every prompt scan
            stores the URLs each provider cited. Those URLs are matched against
            your commented threads by Reddit post ID, so a short link, a mobile
            link, and a full permalink all resolve to the same thread. A match
            records the model and the prompt that produced it. This is the
            outcome that matters, and it&apos;s highlighted accordingly.
          </li>
          <li>
            <strong className="text-ink">In thread.</strong> The rescan re-reads
            flagged threads looking for your brand. If your reply landed and the
            thread now names you, the status flips without a separate scan.
          </li>
        </ol>
        <Callout label="Why this matters">
          A Reddit reply is a distribution bet. Without citation tracking you
          have no idea which bets paid. With it, you learn which subreddits and
          which framings actually get pulled into answers, and you stop
          spending time on the ones that don&apos;t.
        </Callout>
      </Section>

      <Section heading="What gets filtered out" id="filters">
        <p>
          A list of threads you can&apos;t post in is worse than no list. Every
          candidate passes these gates before it appears, and the excluded
          counts are shown at the bottom of the page.
        </p>
        <ComparisonTable
          headers={["Filter", "Rule", "Why"]}
          rows={[
            ["Archived or locked", "Excluded entirely", "Reddit blocks new replies"],
            ["Older than 180 days", "Excluded", "Nobody reads it, nothing cites it"],
            ["90–180 days old", "Must show recent activity", "Stale threads don't convert"],
            ["No competitor named", "Excluded", "It isn't a gap, it's just a thread"],
            ["You're already mentioned", "Moves to Gaps Closed", "The work is done"],
            ["Already commented", "Moves to tracking", "Keeps the open list honest"],
          ]}
        />
      </Section>

      <Section heading="How the competitor set is derived" id="method">
        <p>
          Monroya doesn&apos;t search Reddit for a category keyword and hope.
          It asks the providers your buyers use who they recommend in your
          category, takes the names that come back, normalizes them (stripping
          TLDs and suffixes so &ldquo;Acme.io&rdquo; and &ldquo;Acme&rdquo;
          match), and searches Reddit for those names round-robin so no single
          competitor eats the query budget.
        </p>
        <p>
          Your manually tracked competitors are always included. The AI-derived
          names fill in the rivals you didn&apos;t know models were naming, the
          ones costing you answers you never saw.
        </p>
      </Section>

      <Section heading="Plans" id="plans">
        <ComparisonTable
          headers={["", "Starter $79", "Growth $199", "Intelligence $299"]}
          rows={[
            ["Reddit Gaps", "Not included", "Top 10 threads", "Every thread found"],
            ["Engagement data", "-", "✓", "✓"],
            ["Subreddit rule checks", "-", "✓", "✓"],
            ["Commented + citation tracking", "-", "✓", "✓"],
            ["CSV export", "-", "Not included", "✓"],
          ]}
        />
        <p>
          Everyone starts on the free plan,
          no card until day 8. See{" "}
          <Link
            to="/pricing"
            className="underline decoration-border underline-offset-4 hover:decoration-ink"
          >
            pricing
          </Link>{" "}
          for the full comparison.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link
            to="/onboarding"
            className="inline-flex items-center rounded-md brand-gradient px-5 py-3 text-sm font-medium text-white"
          >
            Start free
          </Link>
          <Link
            to="/reddit-check"
            className="inline-flex items-center rounded-md border border-border bg-paper px-5 py-3 text-sm font-medium text-ink transition-colors hover:bg-panel"
          >
            Run a free Reddit gap check
          </Link>
        </div>
      </Section>
    </MarketingPage>
  );
}
